import { useState, useEffect, useRef, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import Sidebar from './Sidebar';
import { getVadNext, getVadAudioUrl, submitVadAnnotations, skipVadFile } from '../services/api';
import {
  LABELS,
  EXCLUSIVE_LABELS,
  initialSegments,
  splitSegments,
  mergeSegments,
  moveBoundary,
  toggleLabel,
  allLabeled,
  toAnnotations,
} from '../utils/vadSegments';

// ── Label styling ──────────────────────────────────────────────────────────
const LABEL_COLOR = {
  kid:      '#22c55e',
  adult:    '#3b82f6',
  doro:     '#a855f7',
  noise:    '#9ca3af',
  silence:  '#14b8a6',
  notsure: '#f59e0b',
};

// Background tint for a waveform band based on its labels (read-only; tagging
// happens in the list below). Unlabeled → red tint; single → that color; multi
// → diagonal stripes of each label color.
function bandBg(labels) {
  if (!labels.length) return 'rgba(248,113,113,0.14)';
  const hexA = (hex, a) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  };
  if (labels.length === 1) return hexA(LABEL_COLOR[labels[0]], 0.28);
  const stops = labels.flatMap((l, i) => {
    const a = (i / labels.length) * 100, b = ((i + 1) / labels.length) * 100;
    return [`${hexA(LABEL_COLOR[l], 0.32)} ${a}%`, `${hexA(LABEL_COLOR[l], 0.32)} ${b}%`];
  });
  return `repeating-linear-gradient(45deg, ${stops.join(', ')})`;
}

function fmt(sec) {
  if (!isFinite(sec)) return '0:00.0';
  const m = Math.floor(sec / 60);
  const s = (sec % 60).toFixed(1).padStart(4, '0');
  return `${m}:${s}`;
}

export default function VADAnnotation({ user, onPageChange }) {
  const containerRef = useRef(null);   // WaveSurfer mounts here
  const scrollRef    = useRef(null);   // horizontal scroll viewport (auto-follows playhead)
  const overlayRef   = useRef(null);   // segment bands + boundary markers
  const wsRef        = useRef(null);
  const durationRef  = useRef(0);
  const dragRef      = useRef(null);   // { boundaryIndex } while dragging a marker

  const [item, setItem]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [done, setDone]               = useState(false);
  const [playing, setPlaying]         = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const [zoom, setZoom]               = useState(1);     // width multiplier (1 = fit)
  const [rate, setRate]               = useState(1);
  const [segments, setSegments]       = useState([]);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);

  // ── WaveSurfer init (once): render + playback ONLY, no RegionsPlugin ──────
  useEffect(() => {
    if (!containerRef.current) return;
    let destroyed = false;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#5b6675',     // neutral grey so colored segment bands read clearly
      progressColor: '#828d9e',
      cursorColor: '#ffffff',
      cursorWidth: 2,
      height: 130,
      fillParent: true,
      normalize: true,
      interact: false,   // seeking is transport-only; waveform is the labeling surface
    });
    wsRef.current = ws;

    ws.on('ready', () => {
      if (destroyed) return;
      const d = ws.getDuration();
      durationRef.current = d;
      setDuration(d);
      setSegments(initialSegments(d));
      setAudioLoading(false);
    });
    ws.on('timeupdate', t => { if (!destroyed) setCurrentTime(t); });
    ws.on('play',   () => { if (!destroyed) setPlaying(true);  });
    ws.on('pause',  () => { if (!destroyed) setPlaying(false); });
    ws.on('finish', () => { if (!destroyed) setPlaying(false); });

    return () => {
      destroyed = true;
      ws.destroy();
      wsRef.current = null;
    };
  }, []);

  useEffect(() => { wsRef.current?.setPlaybackRate(rate); }, [rate]);

  // ── Auto-follow: keep the playback cursor centered while playing ──────────
  // Only acts when zoomed in (the box overflows its viewport). scrollLeft is
  // clamped to [0, max], so near the clip's start/end the cursor drifts off-
  // center toward the edge instead of leaving blank space.
  useEffect(() => {
    if (!playing) return;
    const el = scrollRef.current;
    const d = durationRef.current;
    if (!el || !d) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 0) return;                       // not zoomed — nothing to scroll
    const cursorX = (currentTime / d) * el.scrollWidth;
    el.scrollLeft = Math.max(0, Math.min(max, cursorX - el.clientWidth / 2));
  }, [currentTime, playing]);

  // ── Boundary drag (pointer move/up handled on window) ─────────────────────
  useEffect(() => {
    function onMove(e) {
      const drag = dragRef.current;
      if (!drag || !overlayRef.current) return;
      const rect = overlayRef.current.getBoundingClientRect();
      const t = ((e.clientX - rect.left) / rect.width) * durationRef.current;
      setSegments(segs => moveBoundary(segs, drag.boundaryIndex, t));
    }
    function onUp() {
      if (dragRef.current) {
        // defer clearing so the synthetic overlay click is suppressed
        setTimeout(() => { dragRef.current = null; }, 0);
      }
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  // ── Keyboard: play/pause + seek ───────────────────────────────────────────
  useEffect(() => {
    function onKey(e) {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.code === 'Space')           { e.preventDefault(); wsRef.current?.playPause(); }
      else if (e.code === 'ArrowLeft')  { e.preventDefault(); wsRef.current?.skip(-2); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); wsRef.current?.skip(2); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Interactions ──────────────────────────────────────────────────────────
  function timeFromEvent(e) {
    const rect = overlayRef.current.getBoundingClientRect();
    return ((e.clientX - rect.left) / rect.width) * durationRef.current;
  }

  function handleOverlayClick(e) {
    if (dragRef.current) return;          // just finished dragging a boundary
    if (!durationRef.current) return;
    setSegments(segs => splitSegments(segs, timeFromEvent(e)));
  }

  function startDrag(boundaryIndex, e) {
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = { boundaryIndex };
  }

  function seekTo(t) {
    const d = durationRef.current;
    if (d > 0) wsRef.current?.seekTo(Math.min(0.999, t / d));
  }

  // ── Load / submit / skip ──────────────────────────────────────────────────
  const loadNext = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSegments([]);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    durationRef.current = 0;
    try {
      const res = await getVadNext();
      const file = res?.file;
      if (!file) { setDone(true); setLoading(false); return; }
      setItem(file);
      setAudioLoading(true);
      setLoading(false);
      wsRef.current?.load(getVadAudioUrl(file.id));
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNext(); }, [loadNext]);

  async function handleSubmit() {
    if (!item || !allLabeled(segments)) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitVadAnnotations(item.id, toAnnotations(segments));
      loadNext();
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSkip() {
    if (item) { try { await skipVadFile(item.id); } catch { /* best effort */ } }
    loadNext();
  }

  // ── Render ──────────────────────────────────────────────────────────────
  const pct = t => `${(t / (durationRef.current || 1)) * 100}%`;
  const ready = duration > 0 && segments.length > 0;

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="vad-annotation"
        onPageChange={onPageChange}
        title={user?.name || user?.email || 'Annotator'}
        subtitle="VAD Annotation"
      />
      <main className="annotator" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="annotator-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <h1 style={{ margin: 0 }}>VAD Annotation</h1>
        </div>

        {done ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <div style={{ fontSize: 48 }}>📭</div>
            <h2>Queue empty</h2>
            <p style={{ color: 'var(--text-muted,#888)' }}>No recordings left to annotate.</p>
            <button className="skip-btn" style={{ width: 'auto', padding: '8px 20px' }}
              onClick={() => { setDone(false); loadNext(); }}>Check again</button>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* File bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>🔊</span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{item ? item.filename : 'Loading…'}</span>
              {duration > 0 && (
                <span className="badge" style={{ background: 'transparent', border: '1px solid var(--border)', marginLeft: 'auto' }}>{fmt(duration)}</span>
              )}
            </div>

            {/* Waveform + segment overlay (same scaled box → always aligned) */}
            <div ref={scrollRef} style={{ position: 'relative', background: '#12161a', borderBottom: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto', flexShrink: 0 }}>
              {audioLoading && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'rgba(10,13,16,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#9ca3af', fontSize: 13 }}>
                  <div style={{ width: 24, height: 24, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'da-spin 0.7s linear infinite' }} />
                  Loading audio…
                </div>
              )}
              <div style={{ position: 'relative', width: `${zoom * 100}%`, minWidth: '100%' }}>
                <div ref={containerRef} />
                {/* Overlay: click to split; bands hold chips; markers drag/merge */}
                <div
                  ref={overlayRef}
                  onClick={handleOverlayClick}
                  style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: ready ? 'crosshair' : 'default' }}
                >
                  {ready && segments.map((seg, i) => (
                    <div key={i}
                      title={seg.labels.join(', ') || 'untagged — tag it in the list below'}
                      style={{
                        position: 'absolute', top: 0, bottom: 0,
                        left: pct(seg.start), width: pct(seg.end - seg.start),
                        boxSizing: 'border-box', background: bandBg(seg.labels),
                      }} />
                  ))}
                  {/* Boundary markers (interior boundaries only) */}
                  {ready && segments.slice(0, -1).map((seg, i) => (
                    <div key={`b${i}`} style={{ position: 'absolute', top: 0, bottom: 0, left: pct(seg.end), transform: 'translateX(-50%)', width: 14, pointerEvents: 'auto' }}>
                      <button
                        onClick={e => { e.stopPropagation(); setSegments(segs => mergeSegments(segs, i)); }}
                        title="Delete boundary (merge)"
                        style={{ position: 'absolute', top: 2, left: '50%', transform: 'translateX(-50%)', background: '#ef4444', border: 'none', color: '#fff', borderRadius: 3, fontSize: 9, lineHeight: '12px', width: 12, height: 12, cursor: 'pointer', padding: 0, zIndex: 2 }}
                      >✕</button>
                      <div
                        onPointerDown={e => startDrag(i, e)}
                        onClick={e => e.stopPropagation()}
                        title="Drag to move boundary"
                        style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 3, background: '#f59e0b', cursor: 'ew-resize' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Transport (seek lives here, decoupled from the labeling surface) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TBtn onClick={() => wsRef.current?.seekTo(0)}>⏮</TBtn>
                <TBtn accent onClick={() => wsRef.current?.playPause()}>{playing ? '⏸' : '▶'}</TBtn>
                <TBtn onClick={() => { wsRef.current?.pause(); wsRef.current?.seekTo(0); }}>⏹</TBtn>
                <span style={{ fontSize: 12, color: 'var(--text-muted,#888)', fontVariantNumeric: 'tabular-nums', minWidth: 110 }}>{fmt(currentTime)} / {fmt(duration)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Zoom</span>
                <input type="range" min="1" max="64" step="0.5" value={zoom}
                  onChange={e => setZoom(Number(e.target.value))} style={{ width: 120, accentColor: 'var(--accent)' }} />
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', minWidth: 32 }}>{zoom}×</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Speed</span>
                {[0.5, 0.75, 1, 1.5, 2].map(r => (
                  <button key={r} onClick={() => setRate(r)} style={{
                    background: rate === r ? 'var(--accent)' : 'transparent',
                    border: `1px solid ${rate === r ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 4, color: 'var(--text)', fontSize: 11, padding: '2px 7px', cursor: 'pointer', fontFamily: 'inherit',
                  }}>{r}×</button>
                ))}
              </div>
            </div>

            {/* Hint bar */}
            <div style={{ padding: '5px 16px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted,#888)', display: 'flex', gap: 18, flexWrap: 'wrap', flexShrink: 0 }}>
              {[['Click waveform', 'Split here'], ['Drag ▌', 'Move boundary'], ['✕', 'Delete boundary'], ['Tag in list', 'below'], ['Space', 'Play/Pause'], ['←/→', 'Seek ±2s']].map(([k, v]) => (
                <span key={k}><strong style={{ color: 'var(--text)' }}>{k}</strong> {v}</span>
              ))}
            </div>

            {/* Segment list — tag / seek / delete each segment */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {ready && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg, #0b0e11)' }}>
                      {['#', 'Start', 'End', 'Dur', 'Labels', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-muted,#888)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {segments.map((seg, i) => {
                      const inRange = currentTime >= seg.start && currentTime < seg.end;
                      return (
                        <tr key={i}
                          onClick={() => seekTo(Math.min(seg.end - 0.0005, seg.start + 0.001))}
                          style={{
                            background: inRange ? 'rgba(58,143,104,0.18)' : 'transparent',
                            cursor: 'pointer',
                          }}>
                          <td style={{ padding: '7px 10px', color: 'var(--text-muted,#888)' }}>#{i + 1}</td>
                          <td style={{ padding: '7px 10px', fontVariantNumeric: 'tabular-nums' }}>{fmt(seg.start)}</td>
                          <td style={{ padding: '7px 10px', fontVariantNumeric: 'tabular-nums' }}>{fmt(seg.end)}</td>
                          <td style={{ padding: '7px 10px', color: 'var(--text-muted,#888)', fontVariantNumeric: 'tabular-nums' }}>{(seg.end - seg.start).toFixed(2)}s</td>
                          <td style={{ padding: '6px 10px' }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {LABELS.map(label => {
                                const active = seg.labels.includes(label);
                                const isExclusive = EXCLUSIVE_LABELS.includes(label);
                                const hasExclusive = seg.labels.some(l => EXCLUSIVE_LABELS.includes(l));
                                const hasOther = seg.labels.some(l => !EXCLUSIVE_LABELS.includes(l));
                                const disabled = isExclusive ? hasOther || (hasExclusive && !active) : hasExclusive;
                                return (
                                  <button key={label}
                                    disabled={disabled}
                                    title={disabled ? "exclusive tags can't be combined with other tags" : label}
                                    onClick={() => { if (!disabled) setSegments(segs => toggleLabel(segs, i, label)); }}
                                    style={{
                                      background: active ? LABEL_COLOR[label] : 'transparent',
                                      border: `1px solid ${LABEL_COLOR[label]}`,
                                      borderRadius: 4, color: active ? '#0b0e11' : LABEL_COLOR[label],
                                      fontSize: 11, fontWeight: active ? 700 : 500, padding: '2px 9px',
                                      cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                                      opacity: disabled ? 0.3 : 1,
                                    }}>{label}</button>
                                );
                              })}
                            </div>
                          </td>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                            {segments.length > 1 && (
                              <button
                                onClick={() => setSegments(segs => mergeSegments(segs, i < segs.length - 1 ? i : i - 1))}
                                title={i < segments.length - 1 ? 'Merge with next' : 'Merge with previous'}
                                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-muted,#888)', fontSize: 11, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' }}
                              >⨉ merge</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer: status + actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted,#888)' }}>
                {ready ? `${segments.length} segment${segments.length > 1 ? 's' : ''}` : ''}
                {ready && !allLabeled(segments) ? ' · some segments untagged' : ''}
              </span>
              {error && <span style={{ color: '#f87171', fontSize: 12 }}>⚠ {error}</span>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <button className="skip-btn" onClick={handleSkip} disabled={loading || submitting || audioLoading} style={{ width: 'auto', padding: '8px 18px' }}>Skip</button>
                <button className="save-btn" onClick={handleSubmit}
                  disabled={!item || loading || submitting || audioLoading || !ready || !allLabeled(segments)}
                  style={{ width: 'auto', padding: '8px 18px' }}>
                  {submitting ? 'Saving…' : 'Submit →'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <style>{`@keyframes da-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function TBtn({ children, onClick, accent }) {
  return (
    <button onClick={onClick} style={{
      background: accent ? 'var(--accent)' : 'transparent',
      border: `1px solid ${accent ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 6, color: accent ? '#fff' : 'var(--text)',
      fontSize: accent ? 14 : 16, width: 34, height: 30,
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    }}>{children}</button>
  );
}
